import { useState } from "react";
import api, { formatApiError } from "../api/client";
import { Headphones, Loader2, Play, Pause } from "lucide-react";

/**
 * Voice-coaching widget — client types an exercise, hears Sopfie's voice
 * explain how to perform it. Lives inside every program folder.
 */
export default function CoachVoice({ defaultExercise, defaultContext, autoOpen = false }) {
  const [exercise, setExercise] = useState(defaultExercise || "");
  const [context, setContext] = useState(defaultContext || "");
  const [transcript, setTranscript] = useState("");
  const [audioSrc, setAudioSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [audioEl, setAudioEl] = useState(null);
  const [playing, setPlaying] = useState(false);

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (!exercise.trim() || busy) return;
    setBusy(true);
    setError("");
    setTranscript("");
    setAudioSrc("");
    try {
      const { data } = await api.post("/ai/coach-voice", {
        exercise: exercise.trim(),
        context: context.trim() || null,
      });
      setTranscript(data.transcript);
      setAudioSrc(`data:audio/mpeg;base64,${data.audio_base64}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePlay = () => {
    if (!audioEl) return;
    if (playing) audioEl.pause();
    else audioEl.play();
  };

  return (
    <div className="card-soft p-6" data-testid="coach-voice">
      <div className="flex items-center gap-2">
        <Headphones size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
        <h3 className="font-serif text-xl text-[color:var(--ink)]">Hear Sopfie coach you</h3>
      </div>
      <p className="text-xs text-[color:var(--ink-2)] mt-1 leading-relaxed">
        Training at home or on holiday? Type any exercise from your program and hear
        Sopfie talk you through it — form cues, where to focus, what to avoid.
      </p>

      <form onSubmit={handleGenerate} className="mt-4 space-y-3">
        <div>
          <label className="overline block mb-1.5">Exercise</label>
          <input
            className="input-warm"
            placeholder="e.g. Barbell back squat, Romanian deadlift…"
            value={exercise}
            onChange={(e) => setExercise(e.target.value)}
            disabled={busy}
            data-testid="coach-voice-exercise"
          />
        </div>
        <div>
          <label className="overline block mb-1.5">Context <span className="text-[color:var(--ink-3)] normal-case tracking-normal">(optional — sets, reps, what to focus on)</span></label>
          <input
            className="input-warm"
            placeholder="e.g. 3 sets x 8 reps, working on depth"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            disabled={busy}
            data-testid="coach-voice-context"
          />
        </div>

        {error && (
          <p className="text-sm text-[color:var(--accent-2)] bg-[color:var(--bg-2)] border border-[color:var(--line-soft)] rounded-lg px-3 py-2" data-testid="coach-voice-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !exercise.trim()}
          className="btn-primary !py-2 !px-5 !text-sm w-full justify-center"
          data-testid="coach-voice-generate"
        >
          {busy ? (
            <>
              <Loader2 size={14} strokeWidth={1.6} className="animate-spin" />
              Sopfie is preparing your cue…
            </>
          ) : (
            <>
              <Headphones size={14} strokeWidth={1.6} />
              Hear coaching
            </>
          )}
        </button>
      </form>

      {audioSrc && (
        <div className="mt-5 pt-5 border-t border-[color:var(--line-soft)]" data-testid="coach-voice-result">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={togglePlay}
              className="h-12 w-12 shrink-0 rounded-full bg-[color:var(--accent)] text-[color:var(--bg)] flex items-center justify-center hover:scale-105 transition-transform shadow-sm"
              data-testid="coach-voice-play"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause size={16} strokeWidth={1.8} /> : <Play size={16} strokeWidth={1.8} className="translate-x-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="overline mb-1">Sopfie says</p>
              <p className="text-[color:var(--ink)] text-sm leading-relaxed italic font-serif" data-testid="coach-voice-transcript">
                "{transcript}"
              </p>
            </div>
          </div>
          <audio
            ref={setAudioEl}
            src={audioSrc}
            autoPlay
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            className="w-full mt-4"
            controls
            data-testid="coach-voice-audio"
          />
        </div>
      )}
    </div>
  );
}
